import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sheet, Spinner, Icon } from '@NP-Webber/crm-kit/dist/next/index.js';
import { OPEN_RELATED_EVENT, openRelatedSettings, readReturn, writeReturn } from './relatedSettingsStore';
import './related.css';

const RelatedSettingsBody = lazy(() => import('./RelatedSettingsBody'));

const SHORTCUT = 'Ctrl+,';

/**
 * RelatedSettingsButton — "הגדרות קשורות" in the top bar.
 * Opens the side panel with the settings that shape the current screen.
 */
export function RelatedSettingsButton() {
  const label = `הגדרות קשורות למסך הזה (${SHORTCUT})`;
  return (
    <button type="button" className="rs-btn" onClick={openRelatedSettings} aria-label={label} title={label}>
      <Icon name="sliders" size={16} />
      <span className="rs-btn__label">הגדרות קשורות</span>
    </button>
  );
}

/**
 * RelatedSettings — mounted once by the shell.
 *   screens     [{ key, label, href }] the screens this user can open (the shell's permission-aware menu) —
 *               a setting is offered only when its screen is in this list
 *   nextDesign  { mode, style, setMode, setStyle } — the "מראה ותצוגה" block
 * Also owns: the Ctrl+, shortcut, opening + highlighting the target card after a jump, and the "חזרה ל…" pill.
 */
export default function RelatedSettings({ screens = [], nextDesign }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);

  useEffect(() => {
    const onOpen = () => { setOpen(true); setEverOpened(true); };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === ',' || e.code === 'Comma')) {
        e.preventDefault();
        setOpen((o) => !o);
        setEverOpened(true);
      }
    };
    window.addEventListener(OPEN_RELATED_EVENT, onOpen);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(OPEN_RELATED_EVENT, onOpen);
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  // a navigation (menu, palette, a jump from the panel) closes it
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <Sheet open={open} onClose={() => setOpen(false)} width={480} ariaLabel="הגדרות קשורות" className="rs-sheet">
        {everOpened ? (
          <Suspense fallback={<div className="rs__busy"><Spinner size={26} /></div>}>
            <RelatedSettingsBody screens={screens} nextDesign={nextDesign} open={open} onClose={() => setOpen(false)} />
          </Suspense>
        ) : null}
      </Sheet>
      <FocusOnArrival />
      <ReturnPill />
    </>
  );
}

/* ── after a jump: open + highlight the card named in location.state.relatedFocus ── */
function normalize(s) {
  return String(s || '').replace(/\s+/g, ' ').trim();
}

function findCard(title) {
  const want = normalize(title);
  const heads = document.querySelectorAll('.ck-panel__toggle, .ck-deck__head, .ck-settings__item, [aria-expanded], .MuiAccordionSummary-root, .MuiTab-root');
  for (const el of heads) {
    const leaves = el.querySelectorAll('b, strong, span, h2, h3, h4, h6, p, div');
    const exact = [...leaves].some((n) => n.childElementCount === 0 && normalize(n.textContent) === want);
    if (exact || normalize(el.textContent).startsWith(want)) return el;
  }
  return null;
}

function FocusOnArrival() {
  const location = useLocation();
  const handled = useRef(null);
  useEffect(() => {
    const title = location.state?.relatedFocus;
    if (!title || handled.current === location.key) return undefined;
    handled.current = location.key;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      const head = findCard(title);
      if (!head && tries < 40) return;
      clearInterval(timer);
      if (!head) return;
      if (head.getAttribute('aria-expanded') === 'false' || head.classList.contains('MuiTab-root')) head.click();
      const card = head.closest('.ck-panel, .ck-deck__item, .ck-settings__item, .MuiAccordion-root') || head;
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      card.classList.add('rs-flash');
      setTimeout(() => card.classList.remove('rs-flash'), 2600);
    }, 150);
    return () => clearInterval(timer);
  }, [location.key, location.state]);
  return null;
}

/* ── "חזרה ל«…»" — after jumping to a setting, one click back to the screen you came from ── */
function ReturnPill() {
  const location = useLocation();
  const navigate = useNavigate();
  const [ret, setRet] = useState(() => readReturn());
  useEffect(() => {
    const cur = readReturn();
    if (!cur) { setRet(null); return; }
    const onTarget = location.pathname === cur.to || location.pathname.startsWith(`${cur.to}/`);
    if (!onTarget) { writeReturn(null); setRet(null); return; } // back home, or wandered elsewhere
    setRet(cur);
  }, [location.pathname]);
  if (!ret) return null;
  const dismiss = () => { writeReturn(null); setRet(null); };
  return (
    <div className="rs-return" role="status">
      <button type="button" className="rs-return__go" onClick={() => { dismiss(); navigate(ret.from); }}>
        <Icon name="right" size={15} />
        <span>חזרה ל<b>{ret.label ? `«${ret.label}»` : 'מסך הקודם'}</b></span>
      </button>
      {ret.setting ? <small className="rs-return__what">{ret.setting}</small> : null}
      <button type="button" className="rs-return__x" aria-label="סגירה" onClick={dismiss}><Icon name="x" size={13} /></button>
    </div>
  );
}
